// js/header-manager.js (VERSÃO FINAL COM PAINÉIS LADO A LADO)

import CartManager from './components/cart-manager.js';
import auth from './auth.js';
import supabase from './supabase-config.js';
import { resolveImagePath } from './utils/pathUtils.js';
import ChatWidgetManager from './modules/ChatWidgetManager.js';
import { sanitizeText } from './utils.js';

const HeaderManager = {
    isUpdating: false,
    notificationChannel: null,
    commandChannel: null,
    lastSeenUpdateInterval: null,

    init() {
        console.log('[HeaderManager] Inicializado com painéis mobile lado a lado.');
        this.cacheDOMElements();

        // Listener principal que reage a login, logout, etc.
        auth.onAuthStateChange(async (event, session) => {
            if (this.isUpdating) {
                console.warn('[HeaderManager] Atualização já em andamento, ignorando evento:', event);
                return;
            }
            this.isUpdating = true;
            
            await this.updateHeaderUI(session);
            
            if (event === 'SIGNED_IN') {
                const currentPath = window.location.pathname;
                if (currentPath.includes('callback') || currentPath.includes('login')) {
                    setTimeout(() => { window.router.navigate('/'); }, 500);
                }
            }
            
            this.isUpdating = false;
        });

        // Ouve por atualizações no carrinho e cliques fora dos menus
        window.addEventListener('cartUpdated', () => this.updateCartCounter());
        this.bindGlobalClickListener();
    },

    cacheDOMElements() {
        this.actionsWrapper = document.querySelector('.header-actions-wrapper');
        this.cartIcon = document.querySelector('#cart-icon-wrapper');
        this.cartCounter = document.querySelector('#cart-item-count');
        this.adminFab = document.querySelector('#admin-fab');
        
        this.notificationsWrapper = document.getElementById('notifications-wrapper');
        this.notificationsBellBtn = document.getElementById('notifications-bell-btn');
        this.notificationsCount = document.getElementById('notifications-count');
        this.notificationsDropdown = document.getElementById('notifications-dropdown');
        this.notificationsList = document.getElementById('notifications-list');
        this.markAllReadBtn = document.getElementById('mark-all-read-btn');
        
        // Captura o botão "Voltar" do painel de notificações mobile
        this.mobileCloseNotificationsBtn = document.getElementById('mobile-close-notifications-btn');
    },

    async updateHeaderUI(session) {
        // Limpa controles de usuário antigos (botões de login ou menu de perfil)
        this.actionsWrapper.querySelectorAll('.auth-controls, .profile-menu-container').forEach(el => el.remove());
        const playOnlineButton = this.actionsWrapper.querySelector('.cta-button');
        
        if (session && session.user) {
            // -- USUÁRIO LOGADO --
            ChatWidgetManager.init(session.user.id);
            this.setupCommandChannel(session.user.id);
            this.updateUserLastSeen(session.user.id);

            const { data: profile } = await supabase.from('profiles').select('username, avatar_url, role').eq('id', session.user.id).single();
            const username = profile?.username || 'Perfil';
            const avatarUrl = resolveImagePath(profile?.avatar_url);

            this.actionsWrapper.insertAdjacentHTML('beforeend', this.createProfileMenuHTML(username, avatarUrl));
            this.bindProfileMenuEvents();
            
            if (playOnlineButton) playOnlineButton.style.display = 'inline-block';
            if (this.cartIcon) this.cartIcon.style.display = 'none'; // Desativado temporariamente
            if (this.adminFab) this.adminFab.style.display = (profile?.role === 'admin') ? 'flex' : 'none';
            
            this.notificationsWrapper.style.display = 'block';
            this.setupNotificationBell(session.user);
            this.updateCartCounter();
        } else {
            // -- USUÁRIO DESLOGADO --
            this.actionsWrapper.insertAdjacentHTML('beforeend', `<div class="auth-controls"><a href="#/login" class="btn btn-secondary">Login</a><a href="#/criar-conta" class="btn btn-primary">Criar Conta</a></div>`);
            
            if (playOnlineButton) playOnlineButton.style.display = 'inline-block';
            if (this.cartIcon) this.cartIcon.style.display = 'none';
            if (this.adminFab) this.adminFab.style.display = 'none';
            if (this.notificationsWrapper) this.notificationsWrapper.style.display = 'none';

            if (this.notificationChannel) supabase.removeChannel(this.notificationChannel);
            if (this.commandChannel) supabase.removeChannel(this.commandChannel);
            this.notificationChannel = null;
            this.commandChannel = null;

            if (this.lastSeenUpdateInterval) {
                clearInterval(this.lastSeenUpdateInterval);
                this.lastSeenUpdateInterval = null;
            }
        }
    },

    setupCommandChannel(currentUserId) {
        if (this.commandChannel) supabase.removeChannel(this.commandChannel);
        this.commandChannel = supabase.channel('chat-commands');
        this.commandChannel
            .on('broadcast', { event: 'open_chat_widget' }, ({ payload }) => {
                if (payload.recipientId === currentUserId) {
                    ChatWidgetManager.openChat(payload.senderId, payload.senderName, payload.senderAvatar);
                }
            })
            .subscribe();
    },
    
    async updateUserLastSeen(userId) {
        if (this.lastSeenUpdateInterval) clearInterval(this.lastSeenUpdateInterval);
        
        const updateStatus = async () => {
            const { error } = await supabase.from('profiles').update({ last_seen_at: new Date().toISOString() }).eq('id', userId);
            if (error) console.error('[HeaderManager] Erro ao atualizar last_seen_at:', error.message);
        };

        // Atualiza imediatamente
        await updateStatus();

        // Agenda atualizações periódicas a cada 2 minutos
        this.lastSeenUpdateInterval = setInterval(updateStatus, 120000);
    },
    
    async setupNotificationBell(user) {
        await this.fetchUnreadCount();
        this.bindNotificationEvents(user.id);
        if (this.notificationChannel) await supabase.removeChannel(this.notificationChannel);
        this.notificationChannel = supabase
            .channel(`public:notifications:user_id=eq.${user.id}`)
            .on('postgres_changes', 
                { 
                    event: 'INSERT', 
                    schema: 'public', 
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                }, 
                () => { this.fetchUnreadCount(); }
            )
            .subscribe();
    },

    async fetchUnreadCount() {
        const { count, error } = await supabase.from('notifications').select('*', { count: 'exact', head: true }).eq('is_read', false);
        if (error) {
            console.error("Erro ao buscar contagem de notificações:", error);
            return;
        }
        this.notificationsCount.textContent = count;
        this.notificationsCount.classList.toggle('hidden', count === 0);
    },

    async fetchNotifications() {
        this.notificationsList.innerHTML = `<p class="placeholder">Carregando...</p>`;
        const { data, error } = await supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(10);
        if (error || !data || data.length === 0) {
            this.notificationsList.innerHTML = `<p class="placeholder">Nenhuma notificação encontrada.</p>`;
            return;
        }
        this.notificationsList.innerHTML = data.map(n => this.renderNotificationItem(n)).join('');
    },

    renderNotificationItem(notification) {
        let icon = '🔔', text = 'Nova notificação.';
        const time = new Date(notification.created_at).toLocaleString('pt-BR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'});
        switch (notification.type) {
            case 'new_like': {
                const safeLiker = sanitizeText(notification.content.liker_username);
                icon = '❤️'; 
                text = `<strong>${safeLiker}</strong> curtiu sua postagem.`; 
                break;
            }
            case 'new_comment': {
                const safeCommenter = sanitizeText(notification.content.commenter_username);
                icon = '💬'; 
                text = `<strong>${safeCommenter}</strong> comentou na sua postagem.`; 
                break;
            }
            case 'new_message': {
                const safeSender = sanitizeText(notification.content.sender_username);
                icon = '✉️'; 
                text = `Você tem uma nova mensagem de <strong>${safeSender}</strong>.`; 
                break;
            }
        }
        return `
            <div class="notification-item-wrapper" data-id="${notification.id}">
                <a href="${notification.link_url}" class="notification-item ${notification.is_read ? '' : 'unread'}">
                    <div class="notification-icon">${icon}</div>
                    <div class="notification-content">
                        <p>${text}</p>
                        <div class="notification-timestamp">${time}</div>
                    </div>
                </a>
                <button class="notification-delete-btn" title="Excluir notificação">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                </button>
            </div>
        `;
    },

    bindNotificationEvents(userId) {
        // --- LÓGICA DE EVENTOS ATUALIZADA PARA O COMPORTAMENTO LADO A LADO ---
        
        // Evento para abrir o painel de notificações
        this.notificationsBellBtn.onclick = (e) => {
            e.stopPropagation();
            
            // Apenas abre/fecha o painel de notificações. Não mexe com o menu sanduíche.
            if (this.notificationsDropdown.classList.toggle('active')) {
                this.fetchNotifications();
            } else {
                this.notificationsList.innerHTML = '';
            }
        };

        // Evento para o botão "Voltar" no painel mobile
        if (this.mobileCloseNotificationsBtn) {
            this.mobileCloseNotificationsBtn.onclick = (e) => {
                e.stopPropagation();
                // Apenas fecha o painel de notificações.
                this.notificationsDropdown.classList.remove('active');
                this.notificationsList.innerHTML = ''; // Limpa o conteúdo para economizar recursos
            };
        }
        // --- FIM DA ATUALIZAÇÃO DA LÓGICA ---

        this.markAllReadBtn.onclick = async () => {
            await supabase.from('notifications').update({ is_read: true }).eq('user_id', userId).eq('is_read', false);
            this.notificationsList.querySelectorAll('.unread').forEach(item => item.classList.remove('unread'));
            this.fetchUnreadCount();
        };

        this.notificationsList.onclick = async (e) => {
            const itemLink = e.target.closest('a.notification-item');
            if (itemLink) {
                const id = itemLink.closest('.notification-item-wrapper').dataset.id;
                await supabase.from('notifications').update({ is_read: true }).eq('id', id);
                this.notificationsDropdown.classList.remove('active');
            }

            const deleteBtn = e.target.closest('.notification-delete-btn');
            if (deleteBtn) {
                e.preventDefault(); e.stopPropagation();
                const wrapper = deleteBtn.closest('.notification-item-wrapper');
                wrapper.style.opacity = '0';
                setTimeout(async () => {
                    await supabase.from('notifications').delete().eq('id', wrapper.dataset.id);
                    this.fetchNotifications();
                    this.fetchUnreadCount();
                }, 300);
            }
        };
    },

    bindGlobalClickListener() {
        document.addEventListener('click', (e) => {
            const profileDropdown = document.querySelector('#profile-dropdown');
            if (profileDropdown?.classList.contains('active') && !e.target.closest('.profile-menu-container')) {
                profileDropdown.classList.remove('active');
            }
            // No mobile, o painel de notificações não deve fechar com cliques externos,
            // apenas com o botão "Voltar", para evitar fechamentos acidentais.
            // Verificamos a largura da tela para aplicar essa regra apenas no desktop.
            if (window.innerWidth > 480) {
                if (this.notificationsDropdown?.classList.contains('active') && !e.target.closest('#notifications-wrapper') && !e.target.closest('#notifications-dropdown')) {
                    this.notificationsDropdown.classList.remove('active');
                    this.notificationsList.innerHTML = '';
                }
            }
        });
    },

    updateCartCounter() {
        if (!this.cartCounter) return;
        const totalItems = CartManager.getTotalItems();
        this.cartCounter.textContent = totalItems;
        this.cartCounter.classList.toggle('hidden', totalItems === 0);
    },

    createProfileMenuHTML(username, avatarUrl) {
        const safeUsername = sanitizeText(username);
        return `
            <div class="profile-menu-container">
                <div class="profile-menu" id="profile-menu-trigger" title="Menu do Usuário">
                    <img src="${avatarUrl}" alt="Avatar de ${safeUsername}" class="profile-menu__avatar">
                    <span class="profile-menu__username">${safeUsername}</span>
                    <span class="dropdown-arrow">▼</span>
                </div>
                <div class="profile-dropdown" id="profile-dropdown">
                    <ul>
                        <li><a href="#/perfil">Meu Perfil</a></li>
                        <li><a href="#/configuracoes">Configurações</a></li>
                        <li class="dropdown-divider"></li>
                        <li><button id="logout-button" class="dropdown-logout-btn">Sair</button></li>
                    </ul>
                </div>
            </div>
        `;
    },

    bindProfileMenuEvents() {
        const trigger = document.querySelector('#profile-menu-trigger');
        const dropdown = document.querySelector('#profile-dropdown');
        const logoutButton = document.querySelector('#logout-button');
        if (!trigger || !dropdown || !logoutButton) return;

        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.classList.toggle('active');
        });
        logoutButton.addEventListener('click', () => {
            auth.signOut();
            window.router.navigate('/');
        });
    }
};

export default HeaderManager;